import * as React from 'react';
import CssBaseline from '@mui/material/CssBaseline';
import Box from '@mui/material/Box';
import Toolbar from '@mui/material/Toolbar';
import Container from '@mui/material/Container';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import AppBar from '../organisms/AppBar';
import Drawer from '../organisms/Drawer';
import Copyright from '../molecules/Copyright';
import Log from '../../lib/log';
import { useSelector } from '../../store/store';
import { Typography } from '@mui/material';
import { useState, useEffect } from 'react';
import { useStripe, useElements, CardElement } from '@stripe/react-stripe-js';
import { StripeCardElement, StripeCardNumberElement } from '@stripe/stripe-js';
import CheckoutForm from '../organisms/CheckoutForm';
import Charges from '../organisms/Charges';
import { v2api } from '../../api/axios';
import CircularProgress from '@mui/material/CircularProgress';

// ログの初期化
const log = new Log('Payments');

function PaymentsContent() {
  log.putLog('info', 'Payments started.');

  // ストアからユーザー情報を取得
  const user = useSelector((state) => state.loginUser.user);

  // カード情報
  const stripe = useStripe();
  const [last4, setLast4] = useState('');
  const [expMonth, setExpMonth] = useState('');
  const [expYear, setExpYear] = useState('');

  useEffect(() => {
    (async () => {
      await getCardInfo(user);
    })();
  }, [user]);

  // カード情報の取得
  const getCardInfo = async (user: any) => {
    try {
      // 最新のカード情報を取得する
      const postData = {
        userId: user.id,
        token: user.access_token,
      };
      const res = await v2api.post('/get-stripe-customer-card', postData);
      if (res.data.statusCode === 200) {
        // 取得成功
        const body = JSON.parse(res.data.body);
        log.putLog('debug', `body: ${console.dir(body)}`);
        setLast4(body.last4);
        setExpMonth(('00' + body.exp_month.toString()).slice(-2));
        setExpYear(('00' + (body.exp_year % 100).toString()).slice(-2));
      } else {
        throw new Error(`getUser error: ${res.data.statusCode}`);
      }
    } catch (err: any) {
      log.putLog('error', err.message ? err.message : err);
    }
  };

  const [updating, setUpdating] = useState(false);

  // カード情報の更新（SetupIntent + 3Dセキュア認証）
  const handleUpdate = async (
    cardElement: StripeCardElement | StripeCardNumberElement,
  ) => {
    try {
      setUpdating(true);

      // ステップ1: SetupIntentのclient_secretを取得
      const setupRes = await v2api.post('/create-setup-intent', {
        userId: user.id,
        token: user.access_token,
      });

      if (setupRes.data.statusCode !== 200) {
        throw new Error('SetupIntentの作成に失敗しました。');
      }

      const { clientSecret } = JSON.parse(setupRes.data.body);

      // ステップ2: 3Dセキュア認証付きでカード情報を確認
      const result = await stripe?.confirmCardSetup(clientSecret, {
        payment_method: {
          card: cardElement as StripeCardElement,
        },
      });

      if (result?.error) {
        throw new Error(result.error.message || 'カード認証に失敗しました。');
      }

      const paymentMethodId = result?.setupIntent?.payment_method;

      // ステップ3: デフォルト決済手段として設定
      await v2api.post('/update-stripe-customer', {
        userId: user.id,
        token: user.access_token,
        paymentMethodId,
      });

      // カード情報のリフレッシュ
      await getCardInfo(user);
      alert('カード情報を更新しました。');
    } catch (err: any) {
      log.putLog('error', err.message);
      alert(err.message || 'カード情報の更新に失敗しました。');
    } finally {
      setUpdating(false);
    }
  };

  // 決済情報

  return (
    <Box sx={{ display: 'flex' }}>
      <CssBaseline />
      <AppBar title='支払情報' />
      <Drawer />
      <Box
        component='main'
        sx={{
          backgroundColor: (theme) =>
            theme.palette.mode === 'light'
              ? theme.palette.grey[100]
              : theme.palette.grey[900],
          flexGrow: 1,
          height: '100vh',
          overflow: 'auto',
        }}
      >
        <Toolbar />
        <Container maxWidth='lg' sx={{ mt: 4, mb: 4 }}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={7} lg={7}>
              <Paper
                sx={{
                  p: 2,
                  display: 'flex',
                  flexDirection: 'column',
                  height: 550,
                }}
              >
                <Typography
                  variant='h6'
                  gutterBottom
                  component='div'
                  color={'gray'}
                >
                  支払履歴
                </Typography>
                <Charges />
              </Paper>
            </Grid>
            <Grid item xs={12} md={5} lg={5}>
              <Paper
                sx={{
                  p: 2,
                  display: 'flex',
                  flexDirection: 'column',
                  height: 'auto',
                }}
              >
                <Typography
                  variant='h6'
                  gutterBottom
                  component='div'
                  color={'gray'}
                >
                  カード情報
                </Typography>
                <CheckoutForm
                  last4={last4}
                  expMonth={expMonth}
                  expYear={expYear}
                  updating={updating}
                  handleUpdate={handleUpdate}
                />
              </Paper>
            </Grid>
          </Grid>
          <Copyright sx={{ pt: 4 }} />
        </Container>
      </Box>
    </Box>
  );
}

export default function Payments() {
  return <PaymentsContent />;
}
